import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Crown, Check, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

/**
 * "Link Pro" subscription card.
 * - Lists active plans (weekly + monthly).
 * - If the caller is already subscribed, shows current plan + cancel button.
 * - Clicking a plan kicks off Stripe Checkout in subscription mode.
 */
export default function SubscriptionCard() {
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState({ status: "none", plan: null });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // plan id being purchased, or "cancel"

  const refresh = useCallback(async () => {
    try {
      const [plansRes, subRes] = await Promise.all([
        axios.get(`${API}/plans`),
        axios.get(`${API}/me/subscription`).catch(() => ({ data: { status: "none", plan: null } })),
      ]);
      setPlans(plansRes.data || []);
      setSub(subRes.data || { status: "none", plan: null });
    } catch (e) {
      console.error("Failed to load plans", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const subscribe = async (planId) => {
    setBusy(planId);
    try {
      const { data } = await axios.post(`${API}/me/subscribe`, {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Could not start checkout");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Subscription failed");
    } finally {
      setBusy(null);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your Link Pro? You'll keep your discount until the current period ends.")) return;
    setBusy("cancel");
    try {
      await axios.post(`${API}/me/subscription/cancel`);
      toast.success("Cancellation scheduled — access continues until period end");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <Card className="bg-[#121212] border-white/5">
        <CardContent className="p-6 flex items-center gap-3 text-white/60">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading Link Pro…
        </CardContent>
      </Card>
    );
  }

  const isActive = sub?.status === "active";
  const activePlan = sub?.plan;
  const periodEnd = sub?.current_period_end
    ? new Date(sub.current_period_end * 1000).toLocaleDateString()
    : null;

  return (
    <div className="space-y-3" data-testid="subscription-card">
      <div className="flex items-center gap-2">
        <Crown className="w-4 h-4 text-[#FFD369]" />
        <p className="text-[10px] uppercase tracking-[0.25em] text-white/60 font-bold">Link Pro</p>
        {isActive && (
          <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px]">
            Active
          </Badge>
        )}
      </div>

      {isActive && activePlan ? (
        <Card className="bg-gradient-to-br from-[#0047FF]/15 to-[#121212] border-[#0047FF]/30" data-testid="active-sub-card">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-baseline justify-between">
              <div>
                <p className="text-lg font-bold text-white" style={{ fontFamily: "Outfit, system-ui" }}>
                  {activePlan.name}
                </p>
                <p className="text-xs text-white/60">
                  {activePlan.discount_pct}% off every booking ·{" "}
                  ${(activePlan.price_cents / 100).toFixed(2)}/{activePlan.interval}
                </p>
              </div>
              {periodEnd && (
                <p className="text-[10px] uppercase tracking-wider text-white/40 font-bold">
                  {sub.cancel_at_period_end ? "Ends" : "Renews"} {periodEnd}
                </p>
              )}
            </div>
            {!sub.cancel_at_period_end && (
              <Button
                variant="outline"
                onClick={cancel}
                disabled={busy === "cancel"}
                className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
                data-testid="cancel-sub-btn"
              >
                <XCircle className="w-4 h-4 mr-2" />
                {busy === "cancel" ? "Cancelling…" : "Cancel at period end"}
              </Button>
            )}
            {sub.cancel_at_period_end && (
              <p className="text-xs text-amber-400">Cancellation scheduled — access remains until {periodEnd}.</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {plans.map((p) => (
            <Card
              key={p.id}
              className={`bg-[#121212] border-white/5 hover:border-[#0047FF]/40 transition-colors ${p.id === 'plus' ? 'sm:border-[#0047FF]/40' : ''}`}
              data-testid={`plan-card-${p.id}`}
            >
              <CardContent className="p-5 space-y-3">
                <div>
                  <p className="text-sm font-bold text-white" style={{ fontFamily: "Outfit, system-ui" }}>
                    {p.name}
                  </p>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-3xl font-black text-white" style={{ fontFamily: "Outfit, system-ui" }}>
                      ${(p.price_cents / 100).toFixed(2)}
                    </span>
                    <span className="text-xs text-white/50">/{p.interval}</span>
                  </div>
                </div>
                <div className="space-y-1.5 text-xs text-white/70">
                  {(p.perks || [`${p.discount_pct}% off every booking`, "Cancel anytime"]).slice(0, 3).map((perk, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span>{perk}</span>
                    </div>
                  ))}
                </div>
                <Button
                  onClick={() => subscribe(p.id)}
                  disabled={busy === p.id}
                  className="w-full bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold rounded-xl border-0"
                  data-testid={`subscribe-${p.id}-btn`}
                >
                  {busy === p.id ? "Starting…" : `Get ${p.name.replace("Link Pro ", "")}`}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
